/** @file Small scene-thumbnail renderer + per-scene cache. */

/** @typedef {import("../core/play.js").Scene} Scene */
/** @typedef {import("../core/play.js").Token} Token */

/**
 * @typedef {object} ThumbCacheEntry
 * @property {string} hash
 * @property {string} dataUrl
 */

/** @type {Map<string, ThumbCacheEntry>} */
const cache = new Map();

/**
 * Deterministic hash of mutable scene content.
 * @param {Scene} s
 * @returns {string}
 */
function sceneHash(s) {
  return JSON.stringify({ t: s.tokens, b: s.ball, v: s.visibleArrowIds });
}

/**
 * @param {Scene} scene
 * @param {{ width: number, height: number, tokens: Token[] }} opts
 * @returns {string} data URL (image/png)
 */
export function renderSceneThumbnail(scene, opts) {
  const hash = sceneHash(scene);
  const cached = cache.get(scene.id);
  if (cached && cached.hash === hash) {
    return cached.dataUrl;
  }

  const { width, height, tokens } = opts;
  const canvas = Object.assign(document.createElement("canvas"), { width, height });
  const ctx = /** @type {CanvasRenderingContext2D | null} */ (canvas.getContext("2d"));
  if (!ctx) {
    const fallback = "data:image/png;base64,";
    cache.set(scene.id, { hash, dataUrl: fallback });
    return fallback;
  }

  // Field background.
  ctx.fillStyle = "#1f2937";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 1;
  ctx.strokeRect(2, 2, width - 4, height - 4);
  // Midline.
  ctx.beginPath();
  ctx.moveTo(width / 2, 2);
  ctx.lineTo(width / 2, height - 2);
  ctx.stroke();

  const pxPerMeterX = (width - 4) / 40;
  const pxPerMeterY = (height - 4) / 20;

  // Tokens as small circles.
  for (const tok of tokens) {
    /** @type {{x:number,y:number} | null} */
    let pos = null;
    if (tok.kind === "ball") {
      pos = scene.ball;
    } else {
      pos = scene.tokens[tok.id] ?? null;
    }
    if (!pos) {
      continue;
    }
    const x = 2 + pos.x * pxPerMeterX;
    const y = 2 + pos.y * pxPerMeterY;
    ctx.beginPath();
    ctx.arc(x, y, tok.kind === "ball" ? 2 : 3, 0, Math.PI * 2);
    ctx.fillStyle =
      tok.kind === "ball" ? "#fbbf24" : tok.team === "home" ? "#3b82f6" : "#ef4444";
    ctx.fill();
  }

  const dataUrl = canvas.toDataURL("image/png");

  cache.set(scene.id, { hash, dataUrl });
  return dataUrl;
}

/**
 * Expose for tests: whether cache already has entry.
 * @param {string} sceneId
 * @returns {string | null}
 */
export function peekCache(sceneId) {
  return cache.get(sceneId)?.hash ?? null;
}

export function __clearCache() {
  cache.clear();
}
